import { create, type StateCreator } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

import { TaskStatus } from '@/app/constants/taskStatus'
import { TaskType } from '@/app/constants/taskType'
import { zustandPlugin } from '@/modules/shared/openreplay'
import type { ActiveTask } from '@/lib/api/config/importTask/active'

/**
 * Tracking of long-running tasks (timesheet / business trips / violations / imports).
 *
 * The split that makes this work:
 *
 *   the SERVER is the source of truth for *what is running* — only it can see the
 *   task a colleague started, and only it survives a refresh;
 *
 *   this store is a cache of that truth PLUS the local payload the server knows
 *   nothing about (`data`), which the completion handlers need in order to download
 *   the produced file, close a print batch, or open the co-driver dialog.
 *
 * Hence `mine`. A task this browser started is persisted together with its payload,
 * so after F5 it resumes with full completion handling. A task someone else started
 * arrives from the server only: it is shown read-only and blocks the button, but must
 * never trigger a download here — the file belongs to the person who asked for it.
 *
 * Task state used to live in a plain redux slice with no persistence at all, so a
 * refresh dropped it entirely and the user queued the same calculation twice.
 */

export type TrackedTask = {
  id: string
  companyId: string
  type: TaskType
  status: TaskStatus
  scopeKey: string | null
  createdAt: string
  createdByUserId: number | null
  createdByLogin: string | null
  progressCurrent?: number
  progressTotal?: number
  message?: string
  logs?: string
  /** True when this browser started it — gates completion side effects. */
  mine: boolean
  /** Only ever set for `mine`; opaque to the server. */
  data?: unknown
}

/**
 * Persisted tasks older than this are dropped on rehydration. The server applies the
 * same window when deciding what still counts as "running", so anything older is
 * either long finished or was reaped.
 */
const PERSIST_TTL_MS = 24 * 60 * 60 * 1000

type State = {
  tasks: Record<string, TrackedTask>
  /** Tasks that finished while we were watching — consumed by completion handlers. */
  completed: TrackedTask[]
}

type Actions = {
  /** Registers a task this browser just started. */
  track: (task: {
    id: string
    companyId: string
    type: TaskType
    scopeKey?: string | null
    data?: unknown
    progressTotal?: number
  }) => void
  /**
   * Adopts a task that is already running — the 409 path. The user asked for work
   * that someone (possibly they themselves, before a refresh) already queued, so we
   * follow the existing task instead of creating a second one.
   */
  adopt: (id: string, companyId: string, type: TaskType, data?: unknown) => void
  /**
   * Reconciles the store with the server's list of what is running right now.
   * Only updates and adds — it never concludes a task is finished, because
   * "absent from the active list" does not say whether it succeeded or failed.
   * Resolving that is the caller's job, via a one-off status lookup.
   */
  syncFromServer: (companyId: string, active: ActiveTask[]) => void
  /** Marks a single task finished, e.g. after a one-off status lookup. */
  settle: (id: string, status: TaskStatus, message?: string, logs?: string) => void
  /** Drops completed tasks once their side effects have run. */
  consumeCompleted: (ids: string[]) => void
  remove: (id: string) => void
  clearCompany: (companyId: string) => void
}

const isTerminal = (status: TaskStatus) =>
  status === TaskStatus.Completed || status === TaskStatus.Error


/**
 * `zustandPlugin` is published as a generic over the store's mutators, but the
 * OpenReplay tracker hands it to us through an `as unknown as` cast that erases the
 * `StoreMutatorIdentifier` constraint. TypeScript then types `set` as the plain
 * two-argument form and every named action below fails to compile.
 *
 * Naming the mutators here restores the devtools three-argument `set`, which is what
 * puts readable action names in both Redux DevTools and the OpenReplay session
 * recording — the reason for using this store in the first place.
 */
type StoreMutators = [['zustand/devtools', never], ['zustand/persist', unknown]]

const trackedCreator = zustandPlugin as unknown as <T>(
  creator: StateCreator<T, StoreMutators, []>,
  name?: string,
) => StateCreator<T, StoreMutators, []>

export const useTaskTrackerStore = create<State & Actions>()(
  devtools(
    persist(
      trackedCreator(
        (set) => ({
          tasks: {},
          completed: [],

          track: (task) =>
            set(
              (s) => ({
                tasks: {
                  ...s.tasks,
                  [task.id]: {
                    id: task.id,
                    companyId: task.companyId,
                    type: task.type,
                    status: TaskStatus.New,
                    scopeKey: task.scopeKey ?? null,
                    createdAt: new Date().toISOString(),
                    createdByUserId: null,
                    createdByLogin: null,
                    progressCurrent: 0,
                    progressTotal: task.progressTotal,
                    mine: true,
                    data: task.data,
                  },
                },
              }),
              false,
              'track',
            ),

          adopt: (id, companyId, type, data) =>
            set(
              (s) => {
                const existing = s.tasks[id]
                return {
                  tasks: {
                    ...s.tasks,
                    [id]: {
                      ...(existing ?? {
                        id,
                        companyId,
                        type,
                        status: TaskStatus.Pending,
                        scopeKey: null,
                        createdAt: new Date().toISOString(),
                        createdByUserId: null,
                        createdByLogin: null,
                      }),
                      // Adopting makes it ours for completion purposes: the user asked
                      // for this work, so they should get its result and its toast.
                      mine: true,
                      data: data ?? existing?.data,
                    },
                  },
                }
              },
              false,
              'adopt',
            ),

          syncFromServer: (companyId, active) =>
            set(
              (s) => {
                const byId = new Map(active.map((t) => [t.id, t]))
                const next: Record<string, TrackedTask> = { ...s.tasks }

                Object.values(s.tasks).forEach((task) => {
                  // Other companies' tasks are left untouched: switching company must
                  // not wipe what is still running where we came from.
                  if (task.companyId !== companyId) return

                  const live = byId.get(task.id)
                  if (!live) return

                  next[task.id] = {
                    ...task,
                    status: live.status,
                    scopeKey: live.scopeKey ?? task.scopeKey,
                    progressCurrent: live.progressCurrent ?? task.progressCurrent,
                    progressTotal: live.progressTotal ?? task.progressTotal,
                    message: live.message ?? task.message,
                    logs: live.logs ?? task.logs,
                    createdByUserId: live.userId ?? task.createdByUserId,
                    createdByLogin: live.userName ?? task.createdByLogin,
                  }
                })

                active.forEach((live) => {
                  if (next[live.id]) return
                  // Started elsewhere — another user, another tab, or this tab before a
                  // refresh. Read-only: no payload, so no downloads on completion.
                  next[live.id] = {
                    id: live.id,
                    companyId: live.companyId,
                    type: live.type,
                    status: live.status,
                    scopeKey: live.scopeKey ?? null,
                    createdAt: live.created ?? new Date().toISOString(),
                    createdByUserId: live.userId ?? null,
                    createdByLogin: live.userName ?? null,
                    progressCurrent: live.progressCurrent ?? undefined,
                    progressTotal: live.progressTotal ?? undefined,
                    message: live.message ?? undefined,
                    logs: live.logs ?? undefined,
                    mine: false,
                  }
                })

                return { tasks: next }
              },
              false,
              'syncFromServer',
            ),

          settle: (id, status, message, logs) =>
            set(
              (s) => {
                const task = s.tasks[id]
                if (!task) return s
                const result = { ...task, status, message, logs: logs ?? task.logs }
                const { [id]: _removed, ...rest } = s.tasks
                if (!isTerminal(status)) {
                  return { tasks: { ...s.tasks, [id]: result } }
                }
                return {
                  tasks: rest,
                  completed: task.mine
                    ? [...s.completed, result]
                    : s.completed,
                }
              },
              false,
              'settle',
            ),

          consumeCompleted: (ids) =>
            set(
              (s) => ({
                completed: s.completed.filter((t) => !ids.includes(t.id)),
              }),
              false,
              'consumeCompleted',
            ),

          remove: (id) =>
            set(
              (s) => {
                const { [id]: _removed, ...rest } = s.tasks
                return { tasks: rest }
              },
              false,
              'remove',
            ),

          clearCompany: (companyId) =>
            set(
              (s) => ({
                tasks: Object.fromEntries(
                  Object.entries(s.tasks).filter(
                    ([, t]) => t.companyId !== companyId,
                  ),
                ),
              }),
              false,
              'clearCompany',
            ),
        }),
        'task-tracker',
      ),
      {
        name: 'tasks.tracker.v1',
        storage: createJSONStorage(() => localStorage),
        // Only our own tasks are persisted, and only the fields the server cannot
        // tell us: identity plus the local payload. Status is deliberately NOT
        // persisted — a stored status is stale by definition, and rehydration
        // always refreshes it from the server.
        partialize: (s) => ({
          tasks: Object.fromEntries(
            Object.entries(s.tasks)
              .filter(([, t]) => t.mine)
              .map(([id, t]) => [
                id,
                {
                  id: t.id,
                  companyId: t.companyId,
                  type: t.type,
                  scopeKey: t.scopeKey,
                  createdAt: t.createdAt,
                  data: t.data,
                } as TrackedTask,
              ]),
          ),
          completed: [] as TrackedTask[],
        }),
        merge: (persisted, current) => {
          const stored = (persisted as State | undefined)?.tasks ?? {}
          const fresh: Record<string, TrackedTask> = {}
          Object.entries(stored).forEach(([id, task]) => {
            const age = Date.now() - new Date(task.createdAt).getTime()
            if (age > PERSIST_TTL_MS) return
            fresh[id] = {
              ...task,
              // Pending until the first sync says otherwise — never trust a
              // persisted status, the task may have finished while we were away.
              status: TaskStatus.Pending,
              mine: true,
              createdByUserId: null,
              createdByLogin: null,
            }
          })
          return { ...current, tasks: fresh, completed: [] }
        },
      },
    ),
    { name: 'EasyTacho', store: 'task-tracker' },
  ),
)

/* ---- selectors ---- */

export const selectCompanyTasks = (companyId: string) => (s: State) =>
  Object.values(s.tasks).filter((t) => t.companyId === companyId)

export const selectHasActiveTasks = (s: State) =>
  Object.values(s.tasks).some((t) => !isTerminal(t.status))

/**
 * The task occupying a given work scope, if any — this is what disables the button
 * and names the person to blame in the tooltip.
 */
export const selectTaskByScope =
  (scopeKey: string | null | undefined) =>
  (s: State): TrackedTask | undefined => {
    if (!scopeKey) return undefined
    return Object.values(s.tasks).find(
      (t) => t.scopeKey === scopeKey && !isTerminal(t.status),
    )
  }

export const isTerminalStatus = isTerminal

/* ---- adapters for the legacy TaskModel shape ---- */

/**
 * The completion handling in TaskStatus.tsx (downloads, print batches, the co-driver
 * dialog) is a thousand lines keyed on the old redux `TaskModel`. `TrackedTask` is a
 * superset of the fields it reads, so consumers get the stored objects as-is.
 *
 * Returning the stored objects — rather than mapping each one into a fresh shape — is
 * what makes these selectors safe to use with `useShallow`: it compares array elements
 * by reference, so a mapper that rebuilds every element on every call reports a change
 * on every render and React aborts with "Maximum update depth exceeded". The banner
 * survived that because its selector only filters; these ones used to map.
 */
export type LegacyTaskModel = TrackedTask

/**
 * Running tasks. Only our own: the handlers in TaskStatus download files and open
 * dialogs, and a colleague's task carries no payload to act on. Their work is surfaced
 * separately, read-only, by RunningTasksBar.
 */
export const selectOwnRunningTasks = (s: State): LegacyTaskModel[] =>
  Object.values(s.tasks).filter((t) => t.mine && !isTerminal(t.status))

/**
 * Zadanie innej firmy rozstrzyga się dopiero po powrocie do niej (useTaskSync pyta
 * tylko o bieżącą) — bez tego filtra wisiałoby na ekranie jako „w toku” bez końca.
 */
export const selectOwnRunningTasksOf =
  (companyId: string | null | undefined) =>
  (s: State): LegacyTaskModel[] =>
    selectOwnRunningTasks(s).filter((t) => !companyId || t.companyId === companyId)

export const selectCompletedTasks = (s: State): LegacyTaskModel[] => s.completed
